import { useContext, useState } from 'react';
import {
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../components/shared/useUser';
import { client, OrderDetails } from '../../app/services/api/orderManagementClient';
import AiOrderExplanationButton from '../../components/shared/orders/AiOrderExplanationButton';
import OrderDetailsPage from './OrderDetails/index';

/**
 * Adds the AI decision inspector without expanding the already-large legacy OrderDetails page.
 * Order details are loaded only when an Admin opens this inspector, keeping the legacy page itself
 * identical to orderly-main and avoiding another conflict-prone integration point.
 */
const OrderDetailsWithAiDecisions = () => {
  const { id } = useParams();
  const { selectedStore, hasPermissionTo, hasPremiumModuleAccess } = useContext(UserContext);
  const { t } = useTranslation('orderDetails');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [orderDetailsError, setOrderDetailsError] = useState(false);

  const canInspectAiDecisions = Boolean(
    hasPermissionTo(['Admin']) &&
    id &&
    selectedStore?.storeNumber &&
    hasPremiumModuleAccess('Predictive Ordering Settings'),
  );

  // Snapshot availability is resolved by the persisted explanation endpoint. Keeping this list
  // independent of OrderlyRepository avoids coupling the feature to the large legacy order query.
  const inspectableItems = orderDetails?.items.filter((item) => item.id > 0) ?? [];
  const selectedItem = inspectableItems.find((item) => item.id === selectedItemId) ?? null;

  const handleOpen = async () => {
    setOpen(true);

    if (orderDetails || loadingOrderDetails || !id || !selectedStore?.storeNumber) {
      return;
    }

    const orderId = Number(id);
    if (!Number.isInteger(orderId) || orderId <= 0) {
      return;
    }

    setOrderDetailsError(false);
    setLoadingOrderDetails(true);
    try {
      const response = await client.getOrder(selectedStore.storeNumber, orderId);
      if (response.success && response.data) {
        setOrderDetails(response.data);
      } else {
        setOrderDetailsError(true);
      }
    } catch {
      setOrderDetailsError(true);
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedItemId(null);
  };

  return (
    <>
      <OrderDetailsPage />

      {canInspectAiDecisions && (
        <ButtonBase
          onClick={() => { void handleOpen(); }}
          aria-label={t('aiDecisions')}
          sx={{
            position: 'fixed',
            right: { xs: 16, sm: 24 },
            bottom: { xs: 16, sm: 24 },
            zIndex: theme.zIndex.speedDial,
            minHeight: 44,
            px: 5,
            borderRadius: 999,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.14)',
            color: 'text.primary',
            gap: 3,
            transition: theme.transitions.create(['transform', 'box-shadow', 'background-color']),
            '&:hover': {
              bgcolor: 'action.hover',
              transform: 'translateY(-1px)',
              boxShadow: '0 14px 36px rgba(15, 23, 42, 0.18)',
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }}
        >
          <AutoAwesomeRoundedIcon sx={{ fontSize: 18 }} />
          <Typography variant="textSM"
            fontWeight={650}>
            {t('aiDecisions')}
          </Typography>
          <ChevronRightRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        </ButtonBase>
      )}

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 480,
            maxWidth: '100vw',
            maxHeight: isMobile ? '90vh' : '100vh',
            '&&': {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            },
            borderTopLeftRadius: isMobile ? 24 : 0,
            borderTopRightRadius: isMobile ? 24 : 0,
          },
        }}
      >
        <Stack sx={{ height: '100%', minHeight: 0 }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={4}
            sx={{ px: 8, pt: 8, pb: 6 }}
          >
            {selectedItem ? (
              <Stack direction="row"
                alignItems="flex-start"
                spacing={4}
                sx={{ minWidth: 0 }}>
                <IconButton
                  aria-label={t('aiDecisions')}
                  size="small"
                  onClick={() => setSelectedItemId(null)}
                  sx={{ ml: -2, mt: -1 }}
                >
                  <ArrowBackRoundedIcon fontSize="small" />
                </IconButton>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="textLG"
                    fontWeight={700}
                    noWrap>
                    {selectedItem.name}
                  </Typography>
                  <Typography
                    variant="textSM"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 2 }}
                    noWrap
                  >
                    {selectedItem.sku}
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row"
                  alignItems="center"
                  spacing={3}>
                  <AutoAwesomeRoundedIcon sx={{ fontSize: 20 }} />
                  <Typography variant="textLG"
                    fontWeight={700}>
                    {t('aiDecisions')}
                  </Typography>
                </Stack>
                <Typography
                  variant="textSM"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 3 }}
                >
                  {t('aiDecisionsSubtitle')}
                </Typography>
              </Box>
            )}

            <IconButton aria-label={t('close')}
              onClick={handleClose}
              sx={{ mt: -1, mr: -2 }}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>

          <Divider />

          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 8, py: 8 }}>
            {loadingOrderDetails && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!loadingOrderDetails && orderDetailsError && (
              <Typography
                variant="textSM"
                color="error.main"
                role="alert"
              >
                {t('aiExplanationLoadError')}
              </Typography>
            )}

            {!loadingOrderDetails && !orderDetailsError && orderDetails && selectedItem && (
              <AiOrderExplanationButton
                key={selectedItem.id}
                inline
                translationNamespace="orderDetails"
                finalQuantity={selectedItem.quantity}
                isQuantityOverridden={selectedItem.isQuantityOverridden}
                primaryUom={selectedItem.primaryUom}
                orderableUom={selectedItem.orderableUom}
                storeNumber={selectedStore?.storeNumber}
                orderId={orderDetails.id}
                orderItemId={selectedItem.id}
              />
            )}

            {!loadingOrderDetails && !orderDetailsError && orderDetails && !selectedItem && inspectableItems.length === 0 && (
              <Typography
                variant="textSM"
                color="text.secondary"
              >
                {t('aiDecisionsEmpty')}
              </Typography>
            )}

            {!loadingOrderDetails && !orderDetailsError && orderDetails && !selectedItem && inspectableItems.length > 0 && (
              <Stack spacing={2}>
                {inspectableItems.map((item) => (
                  <ButtonBase
                    key={item.id}
                    onClick={() => setSelectedItemId(item.id)}
                    sx={{
                      width: '100%',
                      minHeight: 56,
                      px: 4,
                      py: 5,
                      borderRadius: 2,
                      justifyContent: 'space-between',
                      textAlign: 'left',
                      gap: 6,
                      '&:hover': { bgcolor: 'action.hover' },
                      '&:focus-visible': {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="textSM"
                        fontWeight={650}
                        noWrap>
                        {item.name}
                      </Typography>
                      <Typography
                        variant="textXS"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 2 }}
                        noWrap
                      >
                        {item.sku} · {item.quantity} {item.orderableUom}
                      </Typography>
                    </Box>

                    <ChevronRightRoundedIcon sx={{ fontSize: 18, color: 'text.secondary', flex: '0 0 auto' }} />
                  </ButtonBase>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      </Drawer>
    </>
  );
};

export default OrderDetailsWithAiDecisions;

